import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LOCAL_STORAGE_KEYS, url } from "src/app/global/constant";
import { AuthService } from "src/app/services/auth.service";
import { SharedService } from "src/app/services/shared.service";

type InviteState =
  | "loading"
  | "form"
  | "processing"
  | "success"
  | "declined"
  | "error"
  | "expired"
  | "accepted";

@Component({
  selector: "app-dairector-org-invite-accept",
  imports: [CommonModule, FormsModule],
  templateUrl: "./dairector-org-invite-accept.component.html",
  styleUrl: "./dairector-org-invite-accept.component.scss",
})
export class DairectorOrgInviteAcceptComponent implements OnInit {
  state: InviteState = "loading";
  invitationLink: string = "";

  // Invitation data from API
  inviteData: any = null;
  orgName: string = "";
  orgInitial: string = "";
  orgUrl: string = "";
  invitedByName: string = "";
  invitedByRole: string = "";
  invitedByEmail: string = "";
  userRole: string = "";
  teamCount: number = 0;
  activeProjects: number = 0;
  userEmail: string = "";
  expiresIn: string = "";

  // Form fields
  firstname: string = "";
  lastname: string = "";
  password: string = "";
  showPassword: boolean = false;
  passwordStrength: number = 0;
  passwordStrengthLabel: string = "";

  // Decline flow
  showDeclineConfirm: boolean = false;

  // Existing user flag
  isExistingUser: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    public sharedService: SharedService,
  ) { }

  ngOnInit() {
    this.invitationLink = this.route.snapshot.params["invitationlink"] || "";

    if (!this.invitationLink) {
      this.state = "error";
      return;
    }

    this.fetchInviteDetails();
  }

  fetchInviteDetails() {
    this.state = "loading";

    this.authService.getInviteDetails(this.invitationLink).subscribe({
      next: (res: any) => {
        if (!res?.data) {
          this.state = "error";
          return;
        }

        this.inviteData = res.data;
        this.orgName = res.data.company_name || "Organization";
        this.orgInitial = this.orgName.charAt(0).toUpperCase();
        this.orgUrl =
          this.orgName.toLowerCase().replace(/\s+/g, "") + ".dairector.com";
        this.invitedByName = res.data.invited_by_name || "Team Member";
        this.invitedByRole = res.data.invited_by_role || "";
        this.invitedByEmail = res.data.invited_by_email || "";
        this.userRole = res.data.role || "Member";
        this.teamCount = res.data.team_count || 0;
        this.activeProjects = res.data.active_projects || 0;
        this.userEmail = res.data.email || "";
        this.isExistingUser = res.data.is_existing_user || false;
        this.expiresIn = res.data.expires_in || "7 days";
        this.lastname = res.data.matched_user_lastname || "";
        this.firstname = res.data.matched_user_name || "";

        if (res.data.status == "accepted") {
          this.state = "accepted";
        } else if (res.data.status == "declined") {
          this.state = "declined";
        } else if (res.data.status == "expired") {
          this.state = "expired";
        } else {
          this.state = "form";
        }
      },
      error: (err: any) => {
        if (
          err?.error?.statusCode === 410 ||
          err?.error?.message?.includes("expired")
        ) {
          this.state = "expired";
        } else {
          this.state = "error";
        }
      },
    });
  }

  onPasswordChange() {
    const p = this.password;
    if (!p) {
      this.passwordStrength = 0;
      this.passwordStrengthLabel = "";
      return;
    }

    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;

    this.passwordStrength = score;
    const labels = ["", "Weak", "Fair", "Strong", "Excellent"];
    this.passwordStrengthLabel = labels[score] || "";
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  acceptInvite() {
    if (!this.firstname?.trim()) {
      this.sharedService.errorToastNotification("Please enter your first name");
      return;
    }

    if (!this.lastname?.trim()) {
      this.sharedService.errorToastNotification("Please enter your last name");
      return;
    }
    this.state = "processing";

    const payload: any = {
      token: this.invitationLink,
    };

    payload.firstname = this.firstname.trim();
    payload.lastname = this.lastname.trim();

    this.authService.acceptInvite(payload).subscribe({
      next: (res: any) => {
        // this.sharedService.successToastNotification(
        //   "Invitation accepted successfully",
        // );
        this.state = "success";

        // Store user email if available
        this.userEmail = res.data?.email || "";

        this.sharedService.clearAuthStorage();

        this.sharedService.successToastNotification(res.message);
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.sidebar_menu,
          JSON.stringify(res.data?.menues),
        );
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.loginUserDetails,
          JSON.stringify(res.data),
        );

        localStorage.setItem(LOCAL_STORAGE_KEYS.accessToken, res.data?.token);

        const company = res.data?.companies?.[0];
        const companyId = company?.company_id ?? company?.id;
        if (res.data?.companies?.length > 0 && companyId != null && companyId !== "") {
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.selectedCompanyId,
            String(companyId),
          );
          const numericCompanyId = Number(companyId);
          if (Number.isFinite(numericCompanyId) && numericCompanyId > 0) {
            this.sharedService.setCompanyId(numericCompanyId);
          }
        } else {
          localStorage.setItem(LOCAL_STORAGE_KEYS.selectedCompanyId, "-1");
          this.sharedService.setCompanyId(-1);
        }

        this.state = "success";
      },
      error: () => {
        this.state = "form";
        this.sharedService.errorToastNotification(
          "Failed to accept invitation. Please try again.",
        );
      },
    });
  }

  showDecline() {
    this.showDeclineConfirm = true;
  }

  cancelDecline() {
    this.showDeclineConfirm = false;
  }

  confirmDecline() {
    this.authService
      .declineInvite({ invitation_link: this.invitationLink })
      .subscribe({
        next: () => {
          this.state = "declined";
        },
        error: () => {
          this.state = "declined";
        },
      });
  }

  enterWorkspace() {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.loginUserDetails);
      const user = raw ? JSON.parse(raw) : null;
      if (!user?.companies?.length) {
        this.router.navigateByUrl(url.onboarding);
        return;
      }
    } catch {
      // Fall through to dashboard; layout will route if session is incomplete.
    }
    this.router.navigateByUrl(url.dashboard);
  }

  goToLogin() {
    this.router.navigate(["/auth/login"]);
  }
}
